import React from 'react'
import styled from 'styled-components'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { colors } from '@src/theme/sharedComponents'

const Section = styled.section`
  background: ${colors.backgroundAlt};
  padding: 4rem 1.5rem;
  width: 100%;
  border-top: 1px solid ${colors.borderLight};
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const SectionHeader = styled.div`
  margin-bottom: 2.5rem;
`

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.4rem;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 900;
  color: ${colors.textLightMode};
  letter-spacing: -0.03em;
  margin: 0 0 0.35rem;
  line-height: 1;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
`

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${colors.textLightModeMuted};
  margin: 0;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: ${colors.borderLight};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${colors.background};
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const IconWrap = styled.div`
  i {
    font-size: 1.75rem;
    color: ${colors.accent};
  }
`

const CardTitle = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${colors.textLightMode};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
`

const CardText = styled.div`
  font-size: 0.875rem;
  color: ${colors.textLightModeMuted};
  line-height: 1.6;
`

const BenefitsSection: React.FC = () => {
  const { data: s } = usePublicSettings()

  const benefits = [
    { icon: s?.benefit_1_icon, title: s?.benefit_1_title, text: s?.benefit_1_text },
    { icon: s?.benefit_2_icon, title: s?.benefit_2_title, text: s?.benefit_2_text },
    { icon: s?.benefit_3_icon, title: s?.benefit_3_title, text: s?.benefit_3_text },
  ]

  return (
    <Section>
      <Inner>
        <SectionHeader>
          {s?.benefits_eyebrow && <Eyebrow>{s.benefits_eyebrow}</Eyebrow>}
          <SectionTitle>{s?.benefits_title ?? 'Why Work With Us'}</SectionTitle>
          {s?.benefits_subtitle && <SectionSubtitle>{s.benefits_subtitle}</SectionSubtitle>}
        </SectionHeader>
        <Grid>
          {benefits.map((b, i) => (
            <Card key={i}>
              <IconWrap><i className={b.icon ?? 'bi bi-star'} /></IconWrap>
              <CardTitle>{b.title}</CardTitle>
              <CardText>{b.text}</CardText>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Section>
  )
}

export default BenefitsSection
